import {
  useWSModal,
  WSButton,
  WSButtons,
  WSCheckboxToggle,
  WSFormOld,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  AttributeDataType,
  CustomField,
  CustomFieldsValue
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";

import orderBy from "lodash/orderBy";
import { queriesPayees } from "@query";
import {
  CustomFieldValues,
  getDefaultValuesForAdditionalData,
  getYupValidationSchema
} from "../../../../utils/jsonFormSchema";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";

import { selectorPayeeCustomFieldsRequest } from "../../selectors/selectorPayeeCustomFieldsRequest";

export const prepareDynamicFormFields = (
  additionalData: CustomField[],
  prefix?: string
) => {
  return orderBy(additionalData, "dataType", "desc").map(field => {
    const key = field.key ?? "";
    const name = prefix ? `${prefix}.${key}` : key;

    switch (field.dataType) {
      case AttributeDataType.STRING:
        return (
          <WSFormOld.Field
            key={key}
            mb="XL"
            name={name}
            label={`${field.name}${field.isRequired ? "" : " (optional)"}`}
            component={WSTextInput}
          />
        );
      case AttributeDataType.BOOLEAN:
        return (
          <WSFormOld.Field
            key={key}
            mb="XL"
            name={name}
            component={WSCheckboxToggle}
            componentProps={{ label: field.name }}
          />
        );
      default:
        return (
          <WSFormOld.Field
            key={key}
            mb="XL"
            name={name}
            label={`${field.name}${field.isRequired ? "" : " (optional)"}`}
            component={WSTextInput}
          />
        );
    }
  });
};

type Props = {
  payeeId: string;
  externalId?: string;
  customFields?: CustomFieldValues;
  allCustomFields: CustomField[];
  onClose: (result: boolean) => void;
};

const UpdateCustomFieldsModal: React.FC<Props> = props => {
  const xWingspanAccount = useXWingspanPayerId();
  const [updateCustomFields, updateCustomFieldsMeta] =
    queriesPayees.updatePayeeCustomFields.useMutation();
  const [updateExternalId, updateExternalIdMeta] =
    queriesPayees.updatePayee.useMutation();
  const additionalDataValidationSchema = getYupValidationSchema(
    props.allCustomFields
  );

  return (
    <WSFormOld
      defaultValues={{
        externalId: props.externalId,
        ...getDefaultValuesForAdditionalData(
          props.allCustomFields,
          props.customFields
        )
      }}
      onSubmit={async ({ externalId, ...customFields }) => {
        // Custom fields → dedicated v3 endpoint.
        const result = await updateCustomFields({
          payeeId: props.payeeId,
          requestBody: selectorPayeeCustomFieldsRequest({
            fields: props.allCustomFields,
            values: customFields
          }),
          xWingspanAccount
        });

        if (!result) {
          return;
        }

        if (externalId !== props.externalId) {
          await updateExternalId({
            payeeId: props.payeeId,
            payeeUpdate: { externalId },
            xWingspanAccount
          });
        }

        props.onClose(true);
      }}
      validationSchema={Yup.object().shape({
        ...additionalDataValidationSchema
      })}
    >
      <WSFormOld.Field
        name="externalId"
        label="External Contractor ID (optional)"
        component={WSTextInput}
        mb="XL"
      />
      {prepareDynamicFormFields(props.allCustomFields)}
      <ErrorAlert
        mb="XL"
        error={updateCustomFieldsMeta.error || updateExternalIdMeta.error}
        context="Other"
      />
      <WSButtons format="modal">
        <WSButton
          loading={
            updateCustomFieldsMeta.isLoading || updateExternalIdMeta.isLoading
          }
        >
          Update
        </WSButton>
        <WSButton.Tertiary
          onClick={() => {
            props.onClose(false);
          }}
        >
          Cancel
        </WSButton.Tertiary>
      </WSButtons>
    </WSFormOld>
  );
};

export const useUpdateCustomFieldsModal = () =>
  useWSModal(UpdateCustomFieldsModal, {
    title: "Update custom fields",
    size: "S"
  });
